/**
 * @fileoverview Aba "Itens" do editor: o que a IA pede ao cliente, na ordem.
 * Cada item abre para editar mensagens, tentativas e os campos que a IA deve
 * extrair do que chegar. A chave interna sai do nome até o admin mexer nela.
 * Com "itens diferentes em garantia e fora da garantia", a aba mostra uma lista
 * de cada vez; o item vale para uma lista ou para as duas (a garantia vem da OS).
 */

import React, { useState } from "react";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Tooltip from "@material-ui/core/Tooltip";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";

import { i18n } from "../../../../translate/i18n";
import ModernSelect from "../../../../components/ModernSelect";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import useStyles from "./styles";
import { InfoTip } from "./InfoTip";
import {
  FIELD_TYPES,
  INPUT_TYPES,
  ON_MAX,
  WARRANTIES,
  acceptsExtraction,
  inWarrantyList,
  itemsOfWarranty,
  moveWithinList,
  newField,
  newItem,
  slug,
  uniqueKey,
} from "./formState";

const t = (key, params) => i18n.t(`triage.manager.editor.items.${key}`, params);
const g = (key, params) => t(`warranty.${key}`, params);

// Dados da OS com que um campo lido pode ser comparado (diferença vira nota para conferir).
const COMPARAR_COM_OS = ["productModel", "serialNumber", "invoiceNumber", "purchaseDate", "customerDocument"];

const FieldsEditor = ({ item, onChange, workOrdersEnabled = false }) => {
  const classes = useStyles();
  const fields = item.extraction.fields;

  const setField = (uid, patch) => {
    const next = fields.map((f) => {
      if (f.uid !== uid) return f;
      const merged = { ...f, ...patch };
      if (patch.label !== undefined && !f.keyTouched) {
        const usadas = new Set(fields.filter((o) => o.uid !== uid).map((o) => o.key));
        merged.key = uniqueKey(slug(patch.label), usadas);
      }
      return merged;
    });
    onChange({ extraction: { ...item.extraction, fields: next } });
  };

  return (
    <div className={classes.fieldsBox}>
      {fields.map((f) => (
        <div key={f.uid} className={classes.fieldRow}>
          <TextField
            className={classes.field}
            variant="outlined"
            size="small"
            label={t("fieldLabel")}
            value={f.label}
            onChange={(e) => setField(f.uid, { label: e.target.value })}
            inputProps={{ maxLength: 80 }}
          />
          <TextField
            className={classes.field}
            variant="outlined"
            size="small"
            label={t("fieldKey")}
            value={f.key}
            onChange={(e) => setField(f.uid, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""), keyTouched: true })}
            inputProps={{ maxLength: 40 }}
          />
          <ModernSelect
            className={classes.field}
            size="small"
            label={t("fieldType")}
            value={f.type}
            onChange={(e) => setField(f.uid, { type: e.target.value })}
          >
            {FIELD_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {i18n.t(`triage.fieldTypes.${type}`)}
              </MenuItem>
            ))}
          </ModernSelect>
          <div className={classes.fieldFlags}>
            <FormControlLabel
              className={classes.check}
              control={
                <Checkbox size="small" checked={f.required} onChange={(e) => setField(f.uid, { required: e.target.checked })} />
              }
              label={t("fieldRequired")}
            />
            <Tooltip title={t("fieldSensitiveHint")}>
              <FormControlLabel
                className={classes.check}
                control={
                  <Checkbox size="small" checked={f.sensitive} onChange={(e) => setField(f.uid, { sensitive: e.target.checked })} />
                }
                label={t("fieldSensitive")}
              />
            </Tooltip>
            <Tooltip title={t("remove")}>
              <button
                type="button"
                className={classes.iconBtn}
                onClick={() => onChange({ extraction: { ...item.extraction, fields: fields.filter((o) => o.uid !== f.uid) } })}
                aria-label={t("remove")}
              >
                <Trash2 />
              </button>
            </Tooltip>
          </div>
          {workOrdersEnabled && item.inputType !== "text" && (
            <ModernSelect
              className={`${classes.field} ${classes.fieldCompare}`}
              size="small"
              label={t("compareWith")}
              value={f.compareWith || ""}
              onChange={(e) => setField(f.uid, { compareWith: e.target.value })}
              displayEmpty
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">
                <em>{t("compareWithNone")}</em>
              </MenuItem>
              {COMPARAR_COM_OS.map((c) => (
                <MenuItem key={c} value={c}>
                  {t(`compareWithFields.${c}`)}
                </MenuItem>
              ))}
            </ModernSelect>
          )}
        </div>
      ))}
      <div>
        <button
          type="button"
          className={classes.btnGhost}
          onClick={() => onChange({ extraction: { ...item.extraction, fields: [...fields, newField()] } })}
        >
          <Plus /> {t("addField")}
        </button>
      </div>
    </div>
  );
};

// Dados da OS que podem preencher um item de texto (a IA pede para o cliente confirmar).
const DADOS_DA_OS = ["address", "buyer", "product", "purchaseDate", "problem", "invoiceNumber"];

/**
 * Chave "itens diferentes em garantia e fora da garantia", a lista usada sem a
 * garantia na OS e a escolha da lista aberta (com quantos itens cada uma tem).
 */
const WarrantyLists = ({ items, split, onSplitChange, view, onViewChange, workOrdersEnabled }) => {
  const classes = useStyles();
  const [juntar, setJuntar] = useState(false);
  // Itens de uma lista só: desligar junta as listas (a IA passa a pedir todos em toda OS).
  const temItensProprios = items.some((it) => it.warranty);
  const desligar = () => onSplitChange({ ...split, enabled: false }, items.map((it) => ({ ...it, warranty: "" })));

  return (
    <div className={classes.fieldsBox}>
      {/* A regra de funcionamento fica no ⓘ, fora do rótulo (clicar nele não liga a chave). */}
      <div className={classes.titleRow} style={{ marginBottom: 0 }}>
        <FormControlLabel
          className={classes.check}
          control={
            <Switch
              color="primary"
              checked={split.enabled}
              onChange={(e) => {
                if (e.target.checked) onSplitChange({ ...split, enabled: true });
                else if (temItensProprios) setJuntar(true);
                else desligar();
              }}
            />
          }
          label={g("split")}
        />
        <InfoTip text={g("splitInfo")} />
      </div>
      {split.enabled && (
        <>
          <ModernSelect
            className={classes.field}
            size="small"
            label={g("whenUnknown")}
            value={split.whenUnknown}
            onChange={(e) => onSplitChange({ ...split, whenUnknown: e.target.value })}
            InputLabelProps={{ shrink: true }}
            style={{ maxWidth: 320, marginTop: 6 }}
          >
            {WARRANTIES.map((w) => (
              <MenuItem key={w} value={w}>
                {g(`lists.${w}`)}
              </MenuItem>
            ))}
          </ModernSelect>
          {!workOrdersEnabled && <div className={classes.warnText}>{g("noWorkOrders", { list: g(`lists.${split.whenUnknown}`) })}</div>}
          <div className={classes.dayRow} role="group" aria-label={g("listsAria")}>
            {WARRANTIES.map((w) => (
              <button
                key={w}
                type="button"
                aria-pressed={view === w}
                className={`${classes.dayBtn} ${view === w ? classes.dayBtnOn : ""}`}
                onClick={() => onViewChange(w)}
              >
                {g(`lists.${w}`)} · {itemsOfWarranty(items, w).length}
              </button>
            ))}
          </div>
          {/* Recém-ligada, nada separado ainda: como separar. */}
          {!temItensProprios && <div className={classes.hint}>{g("allBoth")}</div>}
        </>
      )}
      <ConfirmationModal
        title={g("turnOffTitle")}
        open={juntar}
        onClose={() => setJuntar(false)}
        onConfirm={() => {
          setJuntar(false);
          desligar();
        }}
        confirmLabel={g("turnOffConfirm")}
      >
        {g("turnOffText", { count: items.length })}
      </ConfirmationModal>
    </div>
  );
};

const ItemsEditor = ({ items, onChange, workOrdersEnabled = false, split, onSplitChange, view, onViewChange }) => {
  const classes = useStyles();
  const separado = split.enabled;
  // Os itens da lista aberta (todos, sem a opção).
  const visiveis = items.filter((it) => !separado || inWarrantyList(it, view));
  const outra = view === "in" ? "out" : "in";

  const update = (uid, patch) => {
    onChange(
      items.map((it) => {
        if (it.uid !== uid) return it;
        const merged = { ...it, ...patch };
        if (patch.name !== undefined && !it.keyTouched) {
          const usadas = new Set(items.filter((o) => o.uid !== uid).map((o) => o.key));
          merged.key = uniqueKey(slug(patch.name), usadas);
        }
        return merged;
      })
    );
  };

  const move = (index, delta) => onChange(moveWithinList(items, visiveis, index, delta));

  // Item das duas listas sai só da lista aberta; o da lista aberta sai da triagem.
  const remover = (it) => (separado && !it.warranty ? update(it.uid, { warranty: outra }) : onChange(items.filter((o) => o.uid !== it.uid)));

  return (
    <div className={classes.form}>
      <WarrantyLists
        items={items}
        split={split}
        onSplitChange={onSplitChange}
        view={view}
        onViewChange={onViewChange}
        workOrdersEnabled={workOrdersEnabled}
      />
      {/* Qual lista está à mostra, em palavras. */}
      {separado && (
        <div className={classes.sectionTitle} style={{ marginBottom: -8 }}>
          {g(view === "in" ? "viewingIn" : "viewingOut")}
        </div>
      )}
      {visiveis.length === 0 && <div className={classes.hint}>{separado ? g("emptyList") : t("empty")}</div>}
      {visiveis.map((it, index) => {
        const tirar = separado && !it.warranty;
        return (
          <div key={it.uid} className={classes.item}>
            <div
              className={classes.itemHead}
              role="button"
              tabIndex={0}
              aria-expanded={it.open}
              aria-label={`${t("expand")}: ${it.name.trim() || t("untitled")}`}
              onClick={() => update(it.uid, { open: !it.open })}
              onKeyDown={(ev) => {
                if (ev.target !== ev.currentTarget) return;
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  update(it.uid, { open: !it.open });
                }
              }}
            >
              <span className={classes.itemOrder}>{index + 1}</span>
              <div className={classes.itemTitle}>
                <div className={`${classes.itemName} ${it.name.trim() ? "" : classes.itemNameEmpty}`}>
                  {it.name.trim() || t("untitled")}
                </div>
                <div className={classes.itemSub}>
                  {i18n.t(`triage.inputTypes.${it.inputType}`)} ·{" "}
                  {it.required ? i18n.t("triage.panel.required") : i18n.t("triage.panel.optional")}
                  {it.extraction.enabled && acceptsExtraction(it.inputType) && ` · ${t("extractsData")}`}
                  {it.isProblem && ` · ${t("isProblem")}`}
                  {tirar && ` · ${g("both")}`}
                </div>
              </div>
              <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center" }}>
                <Tooltip title={t("moveUp")}>
                  <span>
                    <button type="button" className={classes.iconBtn} disabled={index === 0} onClick={() => move(index, -1)} aria-label={t("moveUp")}>
                      <ArrowUp />
                    </button>
                  </span>
                </Tooltip>
                <Tooltip title={t("moveDown")}>
                  <span>
                    <button
                      type="button"
                      className={classes.iconBtn}
                      disabled={index === visiveis.length - 1}
                      onClick={() => move(index, 1)}
                      aria-label={t("moveDown")}
                    >
                      <ArrowDown />
                    </button>
                  </span>
                </Tooltip>
                <Tooltip title={tirar ? g("removeFromList") : t("remove")}>
                  <span>
                    <button type="button" className={classes.iconBtn} onClick={() => remover(it)} aria-label={tirar ? g("removeFromList") : t("remove")}>
                      <Trash2 />
                    </button>
                  </span>
                </Tooltip>
              </div>
              <span className={classes.iconBtn} aria-hidden>
                {it.open ? <ChevronDown /> : <ChevronRight />}
              </span>
            </div>
          {it.open && (
            <div className={classes.itemBody}>
              <div className={classes.grid2}>
                <TextField
                  className={classes.field}
                  variant="outlined"
                  size="small"
                  label={t("name")}
                  placeholder={t("namePlaceholder")}
                  value={it.name}
                  onChange={(e) => update(it.uid, { name: e.target.value })}
                  inputProps={{ maxLength: 80 }}
                  InputLabelProps={{ shrink: true }}
                  autoFocus={!it.name}
                />
                <TextField
                  className={classes.field}
                  variant="outlined"
                  size="small"
                  label={t("key")}
                  helperText={t("keyHint")}
                  value={it.key}
                  onChange={(e) =>
                    update(it.uid, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""), keyTouched: true })
                  }
                  inputProps={{ maxLength: 40 }}
                  InputLabelProps={{ shrink: true }}
                />
              </div>
              <div className={classes.grid2}>
                <ModernSelect
                  className={classes.field}
                  size="small"
                  label={t("inputType")}
                  value={it.inputType}
                  onChange={(e) => update(it.uid, { inputType: e.target.value })}
                >
                  {INPUT_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {i18n.t(`triage.inputTypes.${type}`)}
                    </MenuItem>
                  ))}
                </ModernSelect>
                <FormControlLabel
                  className={classes.check}
                  control={
                    <Switch
                      color="primary"
                      checked={it.required}
                      onChange={(e) => update(it.uid, { required: e.target.checked })}
                    />
                  }
                  label={t("required")}
                />
              </div>
              {separado && (
                <ModernSelect
                  className={classes.field}
                  size="small"
                  label={g("askIn")}
                  value={it.warranty || ""}
                  onChange={(e) => update(it.uid, { warranty: e.target.value })}
                  displayEmpty
                  InputLabelProps={{ shrink: true }}
                  style={{ maxWidth: 320 }}
                >
                  <MenuItem value="">{g("askInOptions.both")}</MenuItem>
                  {WARRANTIES.map((w) => (
                    <MenuItem key={w} value={w}>
                      {g(`askInOptions.${w}`)}
                    </MenuItem>
                  ))}
                </ModernSelect>
              )}
              <div>
                <FormControlLabel
                  className={classes.check}
                  control={
                    <Switch
                      color="primary"
                      checked={!!it.isProblem}
                      onChange={(e) => update(it.uid, { isProblem: e.target.checked })}
                    />
                  }
                  label={t("isProblem")}
                />
                <div className={classes.hint}>{t("isProblemHint")}</div>
              </div>
              {workOrdersEnabled && it.inputType === "text" && (
                <ModernSelect
                  className={classes.field}
                  size="small"
                  label={t("workOrderField")}
                  helperText={t("workOrderFieldHint")}
                  value={it.workOrderField || ""}
                  onChange={(e) => update(it.uid, { workOrderField: e.target.value })}
                  displayEmpty
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">
                    <em>{t("workOrderFieldNone")}</em>
                  </MenuItem>
                  {DADOS_DA_OS.map((d) => (
                    <MenuItem key={d} value={d}>
                      {t(`workOrderFields.${d}`)}
                    </MenuItem>
                  ))}
                </ModernSelect>
              )}
              <TextField
                className={classes.field}
                variant="outlined"
                size="small"
                label={t("requestMessage")}
                placeholder={t("requestPlaceholder")}
                value={it.requestMessage}
                onChange={(e) => update(it.uid, { requestMessage: e.target.value })}
                inputProps={{ maxLength: 2000 }}
                InputLabelProps={{ shrink: true }}
                multiline
                minRows={2}
              />
              <TextField
                className={classes.field}
                variant="outlined"
                size="small"
                label={t("retryMessage")}
                value={it.retryMessage}
                onChange={(e) => update(it.uid, { retryMessage: e.target.value })}
                inputProps={{ maxLength: 2000 }}
                multiline
                minRows={2}
              />
              <TextField
                className={classes.field}
                variant="outlined"
                size="small"
                label={t("aiInstruction")}
                placeholder={t("aiInstructionPlaceholder")}
                value={it.aiInstruction}
                onChange={(e) => update(it.uid, { aiInstruction: e.target.value })}
                inputProps={{ maxLength: 2000 }}
                InputLabelProps={{ shrink: true }}
                multiline
                minRows={2}
              />
              <TextField
                className={classes.field}
                variant="outlined"
                size="small"
                label={t("description")}
                value={it.description}
                onChange={(e) => update(it.uid, { description: e.target.value })}
                inputProps={{ maxLength: 2000 }}
              />
              <div className={classes.grid2}>
                <TextField
                  className={classes.field}
                  variant="outlined"
                  size="small"
                  type="number"
                  label={t("maxAttempts")}
                  value={it.maxAttempts}
                  onChange={(e) => update(it.uid, { maxAttempts: e.target.value })}
                  inputProps={{ min: 1, max: 10 }}
                />
                <ModernSelect
                  className={classes.field}
                  size="small"
                  label={t("onMaxAttempts")}
                  value={it.onMaxAttempts}
                  onChange={(e) => update(it.uid, { onMaxAttempts: e.target.value })}
                  disabled={!it.required}
                  helperText={!it.required ? t("optionalOnMax") : " "}
                >
                  {ON_MAX.map((v) => (
                    <MenuItem key={v} value={v}>
                      {t(`onMax.${v}`)}
                    </MenuItem>
                  ))}
                </ModernSelect>
              </div>

              {/* Vídeo e áudio não têm campos (a IA descreve o vídeo e transcreve a fala). */}
              {acceptsExtraction(it.inputType) && (
                <div>
                  <FormControlLabel
                    className={classes.check}
                    control={
                      <Switch
                        color="primary"
                        checked={it.extraction.enabled}
                        onChange={(e) =>
                          update(it.uid, {
                            extraction: {
                              ...it.extraction,
                              enabled: e.target.checked,
                              fields:
                                e.target.checked && it.extraction.fields.length === 0 ? [newField()] : it.extraction.fields,
                            },
                          })
                        }
                      />
                    }
                    label={t("extraction")}
                  />
                  <div className={classes.hint}>{t("extractionHint")}</div>
                </div>
              )}
              {it.extraction.enabled && acceptsExtraction(it.inputType) && (
                <>
                  <TextField
                    className={classes.field}
                    variant="outlined"
                    size="small"
                    type="number"
                    label={t("minConfidence")}
                    value={it.extraction.minConfidence}
                    onChange={(e) => update(it.uid, { extraction: { ...it.extraction, minConfidence: e.target.value } })}
                    inputProps={{ min: 0, max: 100 }}
                    style={{ maxWidth: 220 }}
                  />
                  <FieldsEditor item={it} onChange={(patch) => update(it.uid, patch)} workOrdersEnabled={workOrdersEnabled} />
                </>
              )}
            </div>
          )}
        </div>
        );
      })}
      <div>
        <button
          type="button"
          className={classes.btnGhost}
          onClick={() => onChange([...items.map((o) => ({ ...o, open: false })), newItem(separado ? view : "")])}
        >
          <Plus /> {t("add")}
        </button>
      </div>
    </div>
  );
};

export default ItemsEditor;
